/**
 * ProfileMenu — top-bar avatar popover (ux-overhaul-hld.md §2, §9). Replaces
 * the old sidebar-footer profile card + logout button: identity (name +
 * primary role) and "Sign out" now live in one place, always reachable
 * regardless of sidebar collapse state.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuthV2Store } from '@/features/auth/authV2Store';
import { authApi } from '@/lib/apiV2';

interface ProfileMenuProps {
  /** Org brand color, when available — tints the avatar disc instead of the flat default. */
  brandColor?: string;
}

export function ProfileMenu({ brandColor }: ProfileMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const user = useAuthV2Store((s) => s.user);
  const roles = useAuthV2Store((s) => s.roles);
  const refreshToken = useAuthV2Store((s) => s.refreshToken);
  const logout = useAuthV2Store((s) => s.logout);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = user?.user?.full_name ?? 'User';
  const initials =
    displayName
      .split(' ')
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('') || 'U';
  const roleLabel = roles[0]?.name ?? roles[0]?.code ?? 'Staff';

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  async function handleLogout() {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // logout is best-effort
    }
    logout();
    // Clear the query cache so stale data from the previous user cannot leak
    // to the next user who logs in on the same browser tab.
    queryClient.clear();
    setIsOpen(false);
    navigate('/app/login', { replace: true });
  }

  return (
    <div ref={containerRef} className="relative" data-testid="profile-menu">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        title={displayName}
      >
        {initials}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
            <p className="truncate text-xs text-gray-500">{roleLabel}</p>
          </div>

          <Link
            // /app/profile is NOT a route — it fell through to AppV2Routes'
            // `path="*"` catch-all, which redirects to `students`, so clicking
            // Profile silently landed on Students & Admissions. The real route
            // is my-profile (AppV2Routes.tsx).
            to="/app/my-profile"
            role="menuitem"
            onClick={() => setIsOpen(false)}
            className={cn(
              'flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors',
              'hover:bg-gray-50',
            )}
          >
            <UserIcon className="h-4 w-4 text-gray-400" />
            Profile
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
